
import { VisibilityRounded } from "@mui/icons-material";

import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  TextField,
} from "@mui/material";

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import { AppContext } from "../../App";
import { endpoints } from "../../api/actions/api";
import * as actions from "../../api/actions/pos";

import { Dialog, Table } from "../../layout";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "./ReportPrintButton";

const RefundReport = (props) => {
  const { t } = useTranslation();
  const { _store, _get } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [refunds, setRefunds] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  // Load refunds
  useEffect(() => {
    setRefunds([]);

    if (!_store?.id) {
      return;
    }

    setLoader(true);

    _get(
      endpoints.VoidSell +
        "/GetRefundsByStore/" +
        _store.id,
      null,
      (res) => {
        setLoader(false);

        if (
          res?.status === 200 &&
          Array.isArray(res.result)
        ) {
          setRefunds(res.result);
        } else {
          setRefunds([]);
        }
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  const handleView = (data) => {
    // Use original API data for the details dialog
    const originalRefund = refunds.find(
      (item) => Number(item.id) === Number(data.id)
    );

    if (!originalRefund) return;

    setViewData(originalRefund);
    setShowForm(true);
  };

  const getRefundType = (refund) => {
    switch (Number(refund.refundType)) {
      case 1:
        return t("returnMoney");
      case 2:
        return t("customerCredit");
      case 3:
        return t("reduceBalance");
      default:
        return "-";
    }
  };

  const getRefundMethod = (refund) => {
    if (Number(refund.refundType) !== 1) {
      return "-";
    }

    switch (Number(refund.refundPayType)) {
      case 1:
        return t("cash");
      case 2:
        return t("card");
      case 3:
        return "Stripe";
      case 4:
        return t("check");
      case 7:
        return t("other");
      default:
        return "-";
    }
  };

  // Filter refunds by selected period
  const filteredRefunds = useMemo(() => {
    if (invalidRange) return [];

    return refunds.filter((refund) => {
      if (!fromDate && !toDate) return true;

      if (!refund.date) return false;

      const refundDate = new Date(refund.date);

      if (Number.isNaN(refundDate.getTime())) {
        return false;
      }

      if (fromDate) {
        const from = new Date(
          fromDate + "T00:00:00"
        );

        if (refundDate < from) {
          return false;
        }
      }

      if (toDate) {
        const to = new Date(
          toDate + "T23:59:59.999"
        );

        if (refundDate > to) {
          return false;
        }
      }

      return true;
    });
  }, [refunds, fromDate, toDate, invalidRange]);

  const refundAmount = (refund) =>
    Number(refund.subtotal || 0) +
    Number(refund.tax || 0);

  // Calculate summary totals
  const totalRefunds = filteredRefunds.reduce(
    (total, refund) =>
      total + refundAmount(refund),
    0
  );

  const cashReturned = filteredRefunds
    .filter(
      (refund) =>
        Number(refund.refundType) === 1 &&
        Number(refund.refundPayType) === 1
    )
    .reduce(
      (total, refund) =>
        total + refundAmount(refund),
      0
    );

  const cardReturned = filteredRefunds
    .filter(
      (refund) =>
        Number(refund.refundType) === 1 &&
        Number(refund.refundPayType) === 2
    )
    .reduce(
      (total, refund) =>
        total + refundAmount(refund),
      0
    );

  const customerCredit = filteredRefunds
    .filter(
      (refund) =>
        Number(refund.refundType) === 2
    )
    .reduce(
      (total, refund) =>
        total + refundAmount(refund),
      0
    );

  const checkReturned = filteredRefunds
    .filter(
      (refund) =>
        Number(refund.refundType) === 1 &&
        Number(refund.refundPayType) === 4
    )
    .reduce(
      (total, refund) =>
        total + refundAmount(refund),
      0
    );

  const otherReturned = filteredRefunds
    .filter(
      (refund) =>
        Number(refund.refundType) === 1 &&
        Number(refund.refundPayType) === 7
    )
    .reduce(
      (total, refund) =>
        total + refundAmount(refund),
      0
    );

  const refundCount = filteredRefunds.length;

  // Prepare table rows
  const reportRows = filteredRefunds.map((refund) => ({
    ...refund,

    refundId: refund.id,
    invoiceId: refund.sellId,

    cashier: refund.user
      ? `${refund.user.firstName || ""} ${
          refund.user.lastName || ""
        }`.trim()
      : "-",

    refundTypeName: getRefundType(refund),
    refundMethod: getRefundMethod(refund),

    totalItems: (refund.items || []).reduce(
      (total, item) =>
        total + Number(item.qty || 0),
      0
    ),

    refundTotal: formatCurrency(
      refundAmount(refund),
      _store
    ),

    date: refund.date
      ? new Date(refund.date).toLocaleString()
      : "",
  }));

  // Screen table includes Actions
  const screenColumns = [
    {
      id: "actions",
      label: t("actions"),
    },
    {
      id: "date",
      label: t("date"),
    },
    {
      id: "refundId",
      label: t("refundId"),
    },
    {
      id: "invoiceId",
      label: t("invoiceId"),
    },
    {
      id: "cashier",
      label: t("cashier"),
    },
    {
      id: "refundTypeName",
      label: t("refundType"),
    },
    {
      id: "refundMethod",
      label: t("refundMethod"),
    },
    {
      id: "totalItems",
      label: t("totalItem"),
    },
    {
      id: "refundTotal",
      label: t("refundTotal"),
    },
  ];

  // Print table excludes Actions
  const printColumns = screenColumns.filter(
    (column) => column.id !== "actions"
  );

  const ActionComp = (data) => (
    <Box>
      <Tooltip arrow title={t("viewRefund")}>
        <IconButton
          onClick={() => handleView(data)}
          size="small"
          className="btn-primary mr-5"
        >
          <VisibilityRounded className="fs-18 m-2" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Summary shared by screen and print
  const SummaryContent = () => (
    <>
      <Grid
        container
        spacing={2}
        sx={{ mb: 2 }}
      >
        {[
          {
            label: t("totalRefunds"),
            amount: totalRefunds,
          },
          {
            label: t("cashReturned"),
            amount: cashReturned,
          },
          {
            label: t("cardReturned"),
            amount: cardReturned,
          },
          {
            label: t("customerCredit"),
            amount: customerCredit,
          },
        ].map((item, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            key={index}
          >
            <Card
              variant="outlined"
              sx={{ height: "100%" }}
            >
              <CardContent>
                <Typography variant="body2">
                  {item.label}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold" }}
                >
                  {formatCurrency(
                    item.amount,
                    _store
                  )}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
        }}
      >
        <Typography>
          <strong>
            {t("refundTransactions")}:
          </strong>{" "}
          {refundCount}
        </Typography>

        <Typography>
          <strong>
            {t("checkReturned")}:
          </strong>{" "}
          {formatCurrency(
            checkReturned,
            _store
          )}
        </Typography>

        <Typography>
          <strong>
            {t("otherReturned")}:
          </strong>{" "}
          {formatCurrency(
            otherReturned,
            _store
          )}
        </Typography>
      </Box>
    </>
  );

  return (
    <>
      {/* Screen controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">
          {t("refundReport")}
        </Typography>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("refundReport")}
        />
      </Box>

      {/* Date filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) =>
            setFromDate(e.target.value)
          }
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) =>
            setToDate(e.target.value)
          }
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{ shrink: true }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>
      </Box>

      {/* Normal screen report */}
      <Box
        sx={{
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ mb: 2 }}
        >
          {t("refundReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{ mb: 2 }}
          >
            {t("fromDate")}: {fromDate || "-"}
            {"   "}
            {t("toDate")}: {toDate || "-"}
          </Typography>
        )}

        <SummaryContent />

        {/* Screen table with Search, Pagination, View */}
        <Table
          ActionComp={ActionComp}
          HeaderComp={() => <div />}
          tblHead={screenColumns}
          tblData={reportRows}
        />
      </Box>

      {/* Separate printable report */}
      <Box
        ref={reportRef}
        className="refund-report-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 2,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("refundReport")}
        </Typography>

        {(fromDate || toDate) && (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              textAlign: "center",
            }}
          >
            {t("fromDate")}: {fromDate || "-"}
            {"   —   "}
            {t("toDate")}: {toDate || "-"}
          </Typography>
        )}

        <SummaryContent />

        {/* All refund records without Search/Pagination/View */}
        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={printColumns}
          tblData={reportRows}
        />
      </Box>

      {/* Refund details dialog */}
      {viewData && (
        <Dialog
          show={showForm}
          onClose={() => setShowForm(false)}
          maxWidth="md"
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" mb={2}>
              {t("refundDetails")}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography>
                <strong>
                  {t("refundId")}:
                </strong>{" "}
                {viewData.id}
              </Typography>

              <Typography>
                <strong>
                  {t("invoiceId")}:
                </strong>{" "}
                {viewData.sellId}
              </Typography>

              <Typography>
                <strong>
                  {t("date")}:
                </strong>{" "}
                {viewData.date
                  ? new Date(
                      viewData.date
                    ).toLocaleString()
                  : "-"}
              </Typography>

              <Typography>
                <strong>
                  {t("cashier")}:
                </strong>{" "}
                {viewData.user
                  ? `${
                      viewData.user.firstName || ""
                    } ${
                      viewData.user.lastName || ""
                    }`.trim()
                  : "-"}
              </Typography>

              <Typography>
                <strong>
                  {t("refundType")}:
                </strong>{" "}
                {getRefundType(viewData)}
              </Typography>

              <Typography>
                <strong>
                  {t("refundMethod")}:
                </strong>{" "}
                {getRefundMethod(viewData)}
              </Typography>
            </Box>

            <Table
              HeaderComp={() => <div />}
              tblHead={[
                {
                  id: "productName",
                  label: t("product"),
                },
                {
                  id: "qty",
                  label: t("qty"),
                },
                {
                  id: "priceDisplay",
                  label: t("price"),
                },
                {
                  id: "totalDisplay",
                  label: t("total"),
                },
              ]}
              tblData={(
                viewData.items || []
              ).map((item) => ({
                ...item,

                productName:
                  item.product?.name ||
                  (Number(item.itemType) === 2
                    ? t("lottery")
                    : "-"),

                priceDisplay: formatCurrency(
                  Number(item.price || 0),
                  _store
                ),

                totalDisplay: formatCurrency(
                  Number(item.qty || 0) *
                    Number(item.price || 0),
                  _store
                ),
              }))}
            />

            <Box
              sx={{
                mt: 2,
                textAlign: "right",
                fontSize: 18,
              }}
            >
              <div>
                {t("subtotal")}:{" "}
                <strong>
                  {formatCurrency(
                    Number(
                      viewData.subtotal || 0
                    ),
                    _store
                  )}
                </strong>
              </div>

              <div>
                {t("tax")}:{" "}
                <strong>
                  {formatCurrency(
                    Number(viewData.tax || 0),
                    _store
                  )}
                </strong>
              </div>

              <div>
                {t("refundTotal")}:{" "}
                <strong>
                  {formatCurrency(
                    refundAmount(viewData),
                    _store
                  )}
                </strong>
              </div>
            </Box>

            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setShowForm(false);
                  setViewData(null);
                }}
              >
                {t("close")}
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _get: actions.get,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(RefundReport);
